import { useState } from "react";
import { createReport, type ReportRow } from "../lib/reports";
import type { DnssecSummary } from "../lib/api";
import type { RecordType } from "../lib/recordTypes";
import { Share2 } from "lucide-react";
import { trackFeature } from "../lib/featureAnalytics";

type Props = {
  host: string;
  type: RecordType;
  rows: ReportRow[];
  consensus: Record<string, number>;
  dnssec?: DnssecSummary | null;
};

export function ShareReportButton({ host, type, rows, consensus, dnssec }: Props) {
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const share = async () => {
    trackFeature("share_report", "resolver_results");
    setBusy(true);
    setCopied(false);
    setError(null);
    try {
      const result = await createReport({ host, type, rows, consensus, dnssec });
      const absolute = new URL(result.url, window.location.origin).toString();
      setUrl(absolute);
      try {
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(absolute);
          setCopied(true);
        }
      } catch {
        // The link is still created; the user can open it even if clipboard
        // permissions are unavailable in the current browser context.
      }
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="share-report">
      <button type="button" className="share-report-button" onClick={share} disabled={busy}>
        <Share2 size={16} aria-hidden="true" />
        {busy ? "Creating report..." : copied ? "Report link copied" : url ? "Create new report" : "Share report"}
      </button>
      {url ? (
        <a href={url} className="share-report-link">
          Open report
        </a>
      ) : null}
      {error ? <span className="share-report-error" role="alert">{error}</span> : null}
    </div>
  );
}
